import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { map } from 'rxjs';
import { Observable } from 'rxjs';
import {
  Request,
  RequestAttachment,
  RequestDeal,
  RequestDetail,
  RequestEvent,
  RequestList,
  Inkoopkans,
} from '@api/models/request.model';
import { IndexResponse, ShowResponse } from '@api/models/response.model';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root',
})
export class RequestsService extends BaseService {
  // private http = inject(HttpClient);
  // private appService = inject(AppService);

  constructor() {
    super('requests');
  }

  getRequests(page: number = 1, perPage: number = 50, q: string = '', filters: Record<string, string> = {}): Observable<IndexResponse<RequestList>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    // Add search query if present
    if (q) {
      queryParams.append('q', q);
    }

    // Add all non-empty filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value);
      }
    });

    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }
    return this.http
      .get<IndexResponse<RequestList>>(
        `${environment.apiUrl}/requests?${queryParams.toString()}`,
        {
          headers: {
            'X-Municipality-Id': municipalityId,
          },
        }
      )
      .pipe(map((response) => response));
  }

  getRequest(requestId: string): Observable<RequestDetail> {
    // const municipalityId = this.appService.municipalityId();
    // if (!municipalityId) {
    //   throw new Error('Geen gemeente geselecteerd');
    // }
    return this.http
      .get<ShowResponse<RequestDetail>>(
        `${environment.apiUrl}/requests/${requestId}`,
        // {
        //   headers: {
        //     'X-Municipality-Id': municipalityId,
        //   },
        // }
      )
      .pipe(map((response) => response.data));
  }

  createRequest(request: Request): Observable<Request> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }
    return this.http
      .post<ShowResponse<Request>>(`${environment.apiUrl}/requests`, request, {
        headers: {
          'X-Municipality-Id': municipalityId,
        },
      })
      .pipe(map((response) => response.data));
  }

  updateRequest(requestId: string, request: Request): Observable<Request> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }
    return this.http
      .put<ShowResponse<Request>>(
        `${environment.apiUrl}/requests/${requestId}`,
        request,
        {
          headers: {
            'X-Municipality-Id': municipalityId,
          },
        }
      )
      .pipe(map((response) => response.data));
  }

  createRequestAttachment(requestId: string, document: File): Observable<any> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }

    const formData = new FormData();
    formData.append('file', document); // 'file' should match the backend's expected field name

    return this.http.post<any>(
      `${environment.apiUrl}/requests/${requestId}/attachments`,
      formData,
      {
        headers: {
          'X-Municipality-Id': municipalityId,
          // Don't set Content-Type header - browser will set it automatically with boundary
        },
      }
    );
  }

  getRequestAttachments(requestId: string): Observable<RequestAttachment[]> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }
    return this.http
      .get<{ data: RequestAttachment[] }>(
        `${environment.apiUrl}/requests/${requestId}/attachments`,
        {
          headers: {
            'X-Municipality-Id': municipalityId,
          },
        }
      )
      .pipe(map((response) => response.data));
  }

  downloadRequestAttachment(
    requestId: string,
    attachmentId: string
  ): Observable<Blob> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }
    return this.http.get(
      `${environment.apiUrl}/requests/${requestId}/attachments/${attachmentId}`,
      {
        headers: {
          'X-Municipality-Id': municipalityId,
        },
        responseType: 'blob',
      }
    );
  }

  getEvents(requestId: string): Observable<RequestEvent[]> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }
    return this.http
      .get<IndexResponse<RequestEvent>>(
        `${environment.apiUrl}/requests/${requestId}/events`,
        {
          headers: {
            'X-Municipality-Id': municipalityId,
          },
        }
      )
      .pipe(map((response) => response.data));
  }

  getRequestDeal(requestId: string): Observable<RequestDeal> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }
    return this.http
      .get<ShowResponse<RequestDeal>>(
        `${environment.apiUrl}/requests/${requestId}/deal`,
        {
          headers: {
            'X-Municipality-Id': municipalityId,
          },
        }
      )
      .pipe(map((response) => response?.data || []));
  }

  updateRequestReferences(requestId: string, dealData: { wmo_so_reference?: string; factuurreferentie?: string }): Observable<RequestDeal> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }
    return this.http
      .patch<ShowResponse<RequestDeal>>(
        `${environment.apiUrl}/requests/${requestId}/update-references`,
        dealData,
        {
          headers: {
            'X-Municipality-Id': municipalityId,
          },
        }
      )
      .pipe(map((response) => response?.data || []));
  }

  getRequestDealImages(requestId: string, fileId: string): Observable<Blob> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }

    return this.http.get(`${environment.apiUrl}/requests/${requestId}/image/${fileId}`, {
      headers: {
        'X-Municipality-Id': municipalityId,
      },
      responseType: 'blob'
    });
  }

  getRequestDealFile(requestId: string, fileId: string): Observable<Blob> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }

    return this.http.get(`${environment.apiUrl}/requests/${requestId}/file/${fileId}`, {
      headers: {
        'X-Municipality-Id': municipalityId,
      },
      responseType: 'blob'
    });
  }

  getStatuses(): Observable<any> {
    const headers = this.getHeaders();
    return this.http.get(`${this.apiUrl}/statuses`, {
      headers,
    });
  }

  completeConsulent(requestId: string): Observable<any> {
    const headers = this.getHeaders();
    return this.http.post(`${this.apiUrl}/${requestId}/complete-consulent`, {}, { headers });
  }

  cancelRequest(requestId: string): Observable<any> {
    const headers = this.getHeaders();
    return this.http.post(`${this.apiUrl}/${requestId}/cancel`, {}, { headers });
  }

  checkWmoReference(wmoReference: string): Observable<{ exists: boolean; message: string; request: RequestDetail }> {
    const headers = this.getHeaders()
    return this.http.get<{ exists: boolean; message: string; request: RequestDetail }>(
      `${this.apiUrl}/check-wmo-reference`,
      { params: { wmo_reference: wmoReference }, headers }
    );
  }

  searchExistingContacts(criteria: {
    postal_code?: string;
    house_number?: string;
    wmo_reference?: string;
    bsn?: string;
  }): Observable<any> {
    const headers = this.getHeaders();
    const params: Record<string, string> = {};

    if (criteria.postal_code && criteria.house_number) {
      params['postal_code'] = criteria.postal_code;
      params['house_number'] = criteria.house_number;
    } else if (criteria.wmo_reference) {
      params['wmo_reference'] = criteria.wmo_reference;
    } else if (criteria.bsn) {
      params['bsn'] = criteria.bsn;
    }

    return this.http.get(`${this.apiUrl}/search-existing-contacts`, {
      params,
      headers,
    });
  }

  getInkoopkans(requestId: string): Observable<Inkoopkans> {
    const headers = this.getHeaders();
    return this.http.get<ShowResponse<Inkoopkans>>(`${this.apiUrl}/${requestId}/inkoopkans`, { headers }).pipe(map((response) => response.data));
  }
}
